import { logger } from "../utils/logger";
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { 
  Play, Target, Award, Users, Globe, Sparkles, Star, ArrowRight, Check, TrendingUp, Shield, Zap, FileText, Eye, Cog, Workflow, Rocket, Heart, Trophy, Crown, Gem, CheckCircle, Flag, MapPin, Compass, Calendar, Clock, Map, Navigation, Milestone, Route, ArrowUpCircle,
  Lightbulb, Briefcase, BarChart, PieChart, Activity, Layers, Box, Package, Settings, Cpu, Code, Database, Server, Cloud, Lock, Key
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { TrustedClients } from '../components/sections/TrustedClients';
import { TestimonialsSection } from '../components/sections/TestimonialsSection';
import { VideoPlanner } from '../components/sections/VideoPlanner';
import { BlogPreview } from '../components/sections/BlogPreview';
import { FAQSection } from '../components/sections/FAQSection';
import { aboutAPI } from '../utils/api';
import { getApiUrl, getApiHeaders, getPublicUrl } from '../utils/apiConfig';

// Lazy load heavy components
const CustomVideoPlayer = lazy(() => import('../components/CustomVideoPlayer').then(m => ({ default: m.CustomVideoPlayer })));

const API_URL = getApiUrl();

function extractYouTubeId(input: string): string {
  if (!input || typeof input !== 'string') return '';
  const trimmed = input.trim();
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed)) return trimmed;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/,
    /^https?:\/\/(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/,
    /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match && match[1]) return match[1];
  }
  return '';
}

const iconMap: { [key: string]: any } = {
  Sparkles, Star, Award, Target, Check, Users, Globe, Shield, TrendingUp, Zap, Rocket, Heart,
  Trophy, Crown, Gem, CheckCircle, Flag, MapPin, Compass, Calendar, Clock, Map, Navigation,
  Milestone, Route, ArrowUpCircle, Cog, Lightbulb, Briefcase, BarChart, PieChart, Activity,
  Layers, Box, Package, Settings, Cpu, Code, Database, Server, Cloud, Lock, Key, FileText,
  Eye, Workflow, Play,
};

interface AboutHeroSection {
  id: string;
  badge: string;
  badgeIcon: string;
  title: string;
  highlightedTitle: string;
  description: string;
  primaryCTA: { text: string; link: string; visible: boolean; };
  secondaryCTA: { text: string; link: string; visible: boolean; };
  videoId: string;
  videoAutoplay: boolean;
  videoDelay: number;
  stats: {
    experience: { value: string; label: string; visible: boolean; };
    clients: { value: string; label: string; visible: boolean; };
    rating: { value: string; stars: number; text: string; visible: boolean; };
  };
  visible: boolean;
  updatedAt: string;
}

// ============================================
// SKELETON COMPONENTS
// ============================================

const HeroSkeleton = () => (
  <section className="relative min-h-[600px] flex items-center px-4 sm:px-6 lg:px-8 py-16 sm:py-20 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100" />
    <div className="max-w-6xl mx-auto w-full relative z-10">
      <div className="grid lg:grid-cols-2 gap-10 items-center">
        {/* Left */}
        <div className="space-y-5">
          <div className="h-8 w-52 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="space-y-3">
            <div className="h-12 bg-gray-200 rounded w-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-12 bg-gray-200 rounded w-4/5 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-4 bg-gray-200 rounded w-5/6 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          <div className="flex gap-3">
            <div className="h-11 w-36 bg-gray-200 rounded-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-11 w-36 bg-gray-200 rounded-full relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="h-4 w-24 bg-gray-200 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="h-4 w-40 bg-gray-200 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
        </div>
        {/* Right — video player */}
        <div className="aspect-video bg-gray-200 rounded-2xl relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
    </div>
  </section>
);

const StorySkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
    <div className="max-w-6xl mx-auto space-y-10">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-32 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-9 bg-gray-200 rounded w-80 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="bg-white/80 rounded-3xl p-8 border border-white/60 shadow-2xl">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-xl flex-shrink-0 relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="flex-1 space-y-2">
                  <div className="h-7 w-16 bg-gray-200 rounded relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                  <div className="h-4 w-32 bg-gray-200 rounded relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                  <div className="h-3 w-48 bg-gray-200 rounded relative overflow-hidden">
                    <div className="absolute inset-0 shimmer" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <div className="h-32 bg-gray-200 rounded-2xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
            </div>
            <div className="h-24 bg-gray-200 rounded-2xl relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-48 bg-gray-200 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const PeopleSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-7xl mx-auto space-y-10">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-32 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-9 bg-gray-200 rounded w-72 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="bg-white/80 rounded-3xl p-8 border border-white/60 shadow-2xl">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="aspect-video bg-gray-200 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="space-y-4">
            <div className="h-7 w-48 bg-gray-200 rounded relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-20 bg-gray-200 rounded-xl relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-44 bg-gray-200 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WorkSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
    <div className="max-w-6xl mx-auto space-y-10">
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-32 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-9 bg-gray-200 rounded w-72 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-80 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-56 bg-gray-200 rounded-2xl relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        ))}
      </div>
      <div className="h-48 bg-gray-200 rounded-3xl relative overflow-hidden">
        <div className="absolute inset-0 shimmer" />
      </div>
    </div>
  </section>
);

const DynamicSectionsSkeleton = () => (
  <div className="space-y-4 px-4 sm:px-6 lg:px-8 py-8">
    {[180, 220, 200].map((h, i) => (
      <div key={i} className="w-full bg-gray-100 rounded-2xl relative overflow-hidden" style={{ height: h }}>
        <div className="absolute inset-0 shimmer" />
      </div>
    ))}
  </div>
);

// ============================================
// MAIN COMPONENT
// ============================================

export default function AboutPageNew() {
  // Hero state
  const [heroData, setHeroData] = useState<AboutHeroSection | null>(null);
  const [heroLoading, setHeroLoading] = useState(true);

  // All other section states
  const [storyOverviewData, setStoryOverviewData] = useState<any>(null);
  const [storyJourneyData, setStoryJourneyData] = useState<any>(null);
  const [storyMissionVisionData, setStoryMissionVisionData] = useState<any>(null);
  const [storyCoreValuesData, setStoryCoreValuesData] = useState<any>(null);
  const [peopleOverviewData, setPeopleOverviewData] = useState<any>(null);
  const [peopleExpertiseData, setPeopleExpertiseData] = useState<any>(null);
  const [peopleCultureData, setPeopleCultureData] = useState<any>(null);
  const [workProcessData, setWorkProcessData] = useState<any>(null);
  const [workBenefitsData, setWorkBenefitsData] = useState<any>(null);
  const [sectionsLoading, setSectionsLoading] = useState(true);

  // Section order
  const [sectionOrder, setSectionOrder] = useState<any[]>([]);
  const [orderLoading, setOrderLoading] = useState(true);

  // SEO settings
  const [aboutSettings, setAboutSettings] = useState<any>(null);

  // ============================================
  // SINGLE useEffect — all fetches in parallel
  // ============================================

  useEffect(() => {
    // Fire hero fetch immediately — unblocks the hero section as fast as possible
    fetchHeroData();

    // Fire all remaining fetches in parallel — don't wait for hero
    Promise.all([
      fetchStoryOverviewData(),
      fetchStoryJourneyData(),
      fetchStoryMissionVisionData(),
      fetchStoryCoreValuesData(),
      fetchPeopleOverviewData(),
      fetchPeopleExpertiseData(),
      fetchPeopleCultureData(),
      fetchWorkProcessData(),
      fetchWorkBenefitsData(),
    ]).finally(() => setSectionsLoading(false));

    fetchSectionOrder();

    // Fetch SEO settings
    fetch(`${API_URL}/about/settings`)
      .then(r => r.json())
      .then(r => { if (r.success && r.data) setAboutSettings(r.data); })
      .catch(() => {});

    // Event listeners for admin editor live updates
    const handlers: { [key: string]: () => void } = {
      aboutHeroUpdated: fetchHeroData,
      aboutStoryOverviewUpdated: fetchStoryOverviewData,
      aboutStoryJourneyUpdated: fetchStoryJourneyData,
      aboutStoryMissionVisionUpdated: fetchStoryMissionVisionData,
      aboutStoryCoreValuesUpdated: fetchStoryCoreValuesData,
      aboutPeopleOverviewUpdated: fetchPeopleOverviewData,
      aboutPeopleExpertiseUpdated: fetchPeopleExpertiseData,
      aboutPeopleCultureUpdated: fetchPeopleCultureData,
      aboutWorkProcessUpdated: fetchWorkProcessData,
      aboutWorkBenefitsUpdated: fetchWorkBenefitsData,
      sectionOrderChanged: fetchSectionOrder,
      sectionVisibilityChanged: fetchSectionOrder,
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, []);

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchHeroData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/hero`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setHeroData({
            ...result.data,
            badgeIcon: result.data.badgeIcon || 'Sparkles',
            videoId: extractYouTubeId(result.data.videoId) || ''
          });
        } else {
          setHeroData(getDefaultHeroData());
        }
      } else {
        setHeroData(getDefaultHeroData());
      }
    } catch (error) {
      logger.error('Error fetching About hero data:', error);
      setHeroData(getDefaultHeroData());
    } finally {
      setHeroLoading(false);
    }
  };

  const fetchStoryOverviewData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/story-overview`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setStoryOverviewData(result.data);
        else setStoryOverviewData(getDefaultStoryOverviewData());
      } else {
        setStoryOverviewData(getDefaultStoryOverviewData());
      }
    } catch (error) {
      logger.error('Error fetching About story overview data:', error);
      setStoryOverviewData(getDefaultStoryOverviewData());
    }
  };

  const fetchStoryJourneyData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/story-journey`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setStoryJourneyData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About story journey data:', error);
    }
  };

  const fetchStoryMissionVisionData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/story-mission-vision`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setStoryMissionVisionData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About story mission & vision data:', error);
    }
  };

  const fetchStoryCoreValuesData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/story-core-values`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setStoryCoreValuesData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About story core values data:', error);
    }
  };

  const fetchPeopleOverviewData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/people-overview`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setPeopleOverviewData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About people overview data:', error);
    }
  };

  const fetchPeopleExpertiseData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/people-expertise`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setPeopleExpertiseData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About people expertise data:', error);
    }
  };

  const fetchPeopleCultureData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/people-culture`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setPeopleCultureData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About people culture data:', error);
    }
  };

  const fetchWorkProcessData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/work-process`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setWorkProcessData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About work process data:', error);
    }
  };

  const fetchWorkBenefitsData = async () => {
    try {
      const response = await fetch(`${API_URL}/about/work-benefits`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) setWorkBenefitsData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching About work benefits data:', error);
    }
  };

  const fetchSectionOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/about/section-order`, {
        headers: { 'Content-Type': 'application/json' },
      });
      const result = await response.json();
      logger.log('📦 [SECTION ORDER] Raw API response:', JSON.stringify(result, null, 2));

      if (!result || !result.success || !result.data || !result.data.sections || !Array.isArray(result.data.sections)) {
        logger.warn('⚠️ [SECTION ORDER] Backend not configured yet - using default section order');
        setSectionOrder([
          { id: 'trusted-clients', name: 'Trusted Clients', visible: true, order: 1 },
          { id: 'testimonials', name: 'Testimonials', visible: true, order: 2 },
          { id: 'video-planner', name: 'Video Planner', visible: true, order: 3 },
          { id: 'blog-preview', name: 'Blog Preview', visible: true, order: 4 },
          { id: 'faqs', name: 'FAQs', visible: true, order: 5 },
        ]);
        return;
      }
      setSectionOrder(result.data.sections);
    } catch (error: any) {
      logger.warn('⚠️ [SECTION ORDER] Error:', error.message);
      setSectionOrder([]);
    } finally {
      setOrderLoading(false);
    }
  };

  // ============================================
  // DEFAULT DATA
  // ============================================

  const getDefaultHeroData = (): AboutHeroSection => ({
    id: '1',
    badge: 'About Creabiz Studio',
    badgeIcon: 'Sparkles',
    title: 'Simplifying Complex Ideas Through',
    highlightedTitle: 'Impactful Animation',
    description: 'We help businesses explain products, train teams, and engage customers with <span class="font-bold text-[#E87D3E]">2D & 3D animated videos</span> that deliver measurable results. Trusted by global brands for clarity, creativity, and ROI.',
    primaryCTA: { text: 'Get Started', link: '/contact', visible: true },
    secondaryCTA: { text: 'View Our Work', link: '/videos', visible: true },
    videoId: 'uAHTkGu3XiU',
    videoAutoplay: true,
    videoDelay: 1500,
    stats: {
      experience: { value: '10+', label: 'Years Experience', visible: true },
      clients: { value: '280+', label: 'Happy Clients', visible: true },
      rating: { value: '5.0', stars: 5, text: '5.0 rating from 280+ clients across 18+ industries globally', visible: true },
    },
    visible: true,
    updatedAt: '2023-10-01T12:00:00Z',
  });

  const getDefaultStoryOverviewData = (): any => ({
    id: '1',
    badge: 'Our Story',
    badgeIcon: 'Sparkles',
    title: 'We Are <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">Creabiz Studio</span>',
    description: 'A decade-long journey of transforming complex B2B ideas into compelling visual stories that drive measurable business impact',
    milestones: [
      { year: '2014', title: 'Foundation', desc: 'Started with a mission to simplify complexity', icon: <Target className="w-4 h-4" /> },
      { year: '2017', title: 'Industry Recognition', desc: 'Reached 100+ clients across 10 industries', icon: <Award className="w-4 h-4" /> },
      { year: '2020', title: 'Global Expansion', desc: 'Serving clients in 25+ countries worldwide', icon: <Globe className="w-4 h-4" /> },
      { year: '2024', title: 'Innovation Leader', desc: '650+ videos, 280+ clients, 18+ industries', icon: <TrendingUp className="w-4 h-4" /> }
    ],
    visible: true,
    updatedAt: '2023-10-01T12:00:00Z',
  });

  // ============================================
  // DYNAMIC SECTION RENDERING
  // ============================================

  const getSectionComponent = (sectionId: string): JSX.Element | null => {
    switch (sectionId) {
      case 'trusted-clients': return <TrustedClients key="trusted-clients" />;
      case 'testimonials': return <TestimonialsSection key="testimonials" />;
      case 'video-planner': return <VideoPlanner key="video-planner" />;
      case 'blog-preview': return <BlogPreview key="blog-preview" />;
      case 'faqs': return <FAQSection key="faqs" />;
      default: return null;
    }
  };

  const renderDynamicSections = () => {
    if (orderLoading) {
      return <DynamicSectionsSkeleton />;
    }

    if (sectionOrder.length > 0) {
      const footerSections = ['video-planner', 'blog-preview', 'faqs'];
      return sectionOrder
        .filter((section) => footerSections.includes(section.id))
        .map((section) => {
          if (!section.visible) return null;
          logger.log(`✅ [SECTION ORDER] Rendering section: ${section.name} (id: ${section.id})`);
          return getSectionComponent(section.id);
        });
    }

    return (
      <>
        <VideoPlanner />
        <BlogPreview />
        <FAQSection />
      </>
    );
  };

  const BadgeIcon = heroData ? (iconMap[heroData.badgeIcon] || Sparkles) : Sparkles;
  const StoryOverviewBadgeIcon = storyOverviewData ? (iconMap[storyOverviewData.badgeIcon] || Sparkles) : Sparkles;
  const StoryJourneyTitleIcon = storyJourneyData ? (iconMap[storyJourneyData.titleIcon] || Sparkles) : Sparkles;

  // ============================================
  // RENDER
  // ============================================

  return (
    <>
      <Helmet>
        <title>{aboutSettings?.seo?.metaTitle || 'About Us | Creabiz Studio'}</title>
        <meta name="description" content={aboutSettings?.seo?.metaDescription || 'Learn about Creabiz Studio, our team, mission, and how we create impactful 3D animations for B2B companies worldwide.'} />
        <meta property="og:title" content={aboutSettings?.seo?.ogTitle || aboutSettings?.seo?.metaTitle || 'About Us | Creabiz Studio'} />
        <meta property="og:description" content={aboutSettings?.seo?.ogDescription || aboutSettings?.seo?.metaDescription || 'Learn about Creabiz Studio, our team, mission, and how we create impactful 3D animations for B2B companies worldwide.'} />
        {aboutSettings?.seo?.ogImage && <meta property="og:image" content={aboutSettings.seo.ogImage} />}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content={aboutSettings?.seo?.ogTitle || aboutSettings?.seo?.metaTitle || 'About Us | Creabiz Studio'} />
        <meta property="twitter:description" content={aboutSettings?.seo?.ogDescription || aboutSettings?.seo?.metaDescription || 'Learn about Creabiz Studio.'} />
      </Helmet>

      {/* Hero Section */}
      {heroLoading ? (
        <HeroSkeleton />
      ) : (
        <section className="relative min-h-[600px] flex items-center px-4 sm:px-6 lg:px-8 py-16 sm:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100" />
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#E8B84D]/10 via-[#E87D3E]/8 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#2F2E5C]/8 via-[#4A4878]/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute inset-0 opacity-[0.015]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

          <div className="max-w-6xl mx-auto w-full relative z-10">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              {/* Left Content */}
              <div>
                <div className="inline-flex items-center gap-3 bg-white/60 backdrop-blur-xl px-5 py-2.5 rounded-full mb-6 border border-white/40 shadow-lg shadow-[#E8B84D]/5">
                  <BadgeIcon className="w-5 h-5 text-[#E8B84D]" />
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#2F2E5C] via-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                    {heroData!.badge}
                  </span>
                </div>

                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-[#2F2E5C]">
                  {heroData!.title}
                  {heroData!.highlightedTitle && (
                    <>{' '}<span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{heroData!.highlightedTitle}</span></>
                  )}
                </h1>
                <div
                  className="text-base sm:text-lg text-gray-600 mb-6 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: heroData!.description }}
                />

                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  {heroData!.primaryCTA.visible && (
                    <Link to={heroData!.primaryCTA.link}>
                      <Button className="relative overflow-hidden bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939] hover:from-[#c13939] hover:via-[#D74444] hover:to-[#e85e5e] text-white rounded-full px-6 h-11 gap-2 w-full sm:w-auto shadow-lg shadow-[#D74444]/20 before:absolute before:inset-0 before:bg-white/10 before:opacity-0 hover:before:opacity-100 before:transition-opacity">
                        <span className="relative z-10 flex items-center gap-2">
                          {heroData!.primaryCTA.text}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </Button>
                    </Link>
                  )}
                  {heroData!.secondaryCTA.visible && (
                    <Link to={heroData!.secondaryCTA.link}>
                      <Button variant="outline" className="relative overflow-hidden rounded-full px-6 h-11 gap-2 border-2 border-[#E87D3E] text-[#E87D3E] hover:bg-gradient-to-r hover:from-[#E87D3E] hover:to-[#E8B84D] hover:text-white hover:border-transparent w-full sm:w-auto transition-all duration-300 before:absolute before:inset-0 before:bg-gradient-to-r before:from-[#E87D3E] before:to-[#E8B84D] before:opacity-0 hover:before:opacity-100 before:transition-opacity">
                        <span className="relative z-10 flex items-center gap-2">
                          {heroData!.secondaryCTA.text}
                          <Play className="w-4 h-4" />
                        </span>
                      </Button>
                    </Link>
                  )}
                </div>

                {heroData!.stats.rating.visible && (
                  <div className="flex items-center gap-4">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className={`w-4 h-4 ${star <= heroData!.stats.rating.stars ? 'fill-[#E8B84D] text-[#E8B84D]' : 'text-gray-300'}`} />
                      ))}
                    </div>
                    <span className="text-sm text-gray-600">{heroData!.stats.rating.text}</span>
                  </div>
                )}
              </div>

              {/* Right — Video */}
              <div className="relative">
                {heroData!.videoId && heroData!.videoId.length >= 11 ? (
                  <Suspense fallback={
                    <div className="aspect-video bg-gray-200 rounded-2xl relative overflow-hidden">
                      <div className="absolute inset-0 shimmer" />
                    </div>
                  }>
                    <CustomVideoPlayer
                      videoId={heroData!.videoId}
                      autoplay={heroData!.videoAutoplay}
                      autoplayDelay={heroData!.videoDelay}
                    />
                  </Suspense>
                ) : (
                  <div className="w-full aspect-video bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center border-2 border-dashed border-gray-300">
                    <div className="text-center">
                      <Play className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-500 text-sm">No video configured</p>
                      {heroData!.videoId && (
                        <p className="text-xs text-red-500 mt-1">Invalid video ID: {heroData!.videoId}</p>
                      )}
                    </div>
                  </div>
                )}

                {(heroData!.stats.experience.visible || heroData!.stats.clients.visible) && (
                  <>
                    {heroData!.stats.experience.visible && (
                      <div className="absolute -bottom-3 -left-3 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg p-3 border border-white/40">
                        <div className="text-xl font-bold text-[#2F2E5C]">{heroData!.stats.experience.value}</div>
                        <div className="text-xs text-gray-600">{heroData!.stats.experience.label}</div>
                      </div>
                    )}
                    {heroData!.stats.clients.visible && (
                      <div className="absolute -top-3 -right-3 bg-gradient-to-br from-[#E8B84D] via-[#E87D3E] to-[#d4a03d] rounded-2xl shadow-lg p-3 text-white border border-white/20">
                        <div className="text-xl font-bold">{heroData!.stats.clients.value}</div>
                        <div className="text-xs text-white/90">{heroData!.stats.clients.label}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* TrustedClients */}
      {(!orderLoading && sectionOrder.find(s => s.id === 'trusted-clients')?.visible !== false) && (
        <TrustedClients />
      )}

      {/* Our Story */}
      {sectionsLoading ? (
        <StorySkeleton />
      ) : (
        <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-[#E87D3E] to-[#E8B84D] rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] rounded-full blur-3xl" />
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {storyOverviewData && storyOverviewData.visible !== false && (
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
                  <StoryOverviewBadgeIcon className="w-4 h-4 text-[#E87D3E]" />
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                    {storyOverviewData.badge}
                  </span>
                </div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
                  {storyOverviewData.title}{' '}
                  <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
                    {storyOverviewData.highlightedTitle}
                  </span>
                </h2>
                <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">
                  {storyOverviewData.subtitle}
                </p>
              </div>
            )}

            <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 border border-white/60 shadow-2xl mb-8 overflow-hidden">
              <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-[#E87D3E]/10 to-transparent rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-gradient-to-tr from-[#E8B84D]/10 to-transparent rounded-full blur-2xl" />

              <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-start">
                {storyJourneyData && storyJourneyData.visible !== false && (
                  <div className="space-y-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                        <StoryJourneyTitleIcon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#2F2E5C]">
                        {storyJourneyData.title}
                      </h3>
                    </div>
                    <div
                      className="text-base text-gray-600 leading-relaxed [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold"
                      dangerouslySetInnerHTML={{ __html: storyJourneyData.description }}
                    />
                    <div className="space-y-4">
                      {storyJourneyData.milestones && storyJourneyData.milestones
                        .sort((a: any, b: any) => a.order - b.order)
                        .map((milestone: any) => {
                          const MilestoneIcon = iconMap[milestone.icon] || Star;
                          return (
                            <div key={milestone.id} className="flex items-start gap-4">
                              <div className="flex-shrink-0 w-12 h-12 bg-white rounded-lg shadow-md border border-gray-100 flex items-center justify-center">
                                <MilestoneIcon className="w-5 h-5 text-[#E87D3E]" />
                              </div>
                              <div className="flex-1 pt-1">
                                <div className="text-2xl font-bold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent mb-1">
                                  {milestone.year}
                                </div>
                                <div className="text-base font-bold text-[#2F2E5C] mb-1">{milestone.title}</div>
                                <p className="text-sm text-gray-600 leading-relaxed">{milestone.description}</p>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {storyMissionVisionData && storyMissionVisionData.visible !== false && (
                  <div className="space-y-6">
                    <div className="relative bg-gradient-to-br from-[#2F2E5C] to-[#1a1935] rounded-2xl p-6 text-white overflow-hidden shadow-xl">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16" />
                      <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-3">
                          {(() => {
                            const MissionIcon = iconMap[storyMissionVisionData.mission.icon] || Target;
                            return <MissionIcon className="w-5 h-5 text-[#E8B84D]" />;
                          })()}
                          <span className="text-sm font-bold text-white">{storyMissionVisionData.mission.title}</span>
                        </div>
                        <div
                          className="text-base leading-relaxed text-white [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold"
                          dangerouslySetInnerHTML={{ __html: storyMissionVisionData.mission.text }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      {storyMissionVisionData.impactStats && storyMissionVisionData.impactStats
                        .sort((a: any, b: any) => a.order - b.order)
                        .map((stat: any) => {
                          const StatIcon = iconMap[stat.icon] || Star;
                          return (
                            <div key={stat.id} className="relative bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-white/60 shadow-md hover:shadow-lg hover:-translate-y-1 transition-all group">
                              <div className={`w-8 h-8 bg-gradient-to-br ${stat.gradient} rounded-lg flex items-center justify-center mb-2 text-white group-hover:scale-110 transition-transform`}>
                                <StatIcon className="w-4 h-4" />
                              </div>
                              <div className="text-2xl font-bold text-[#2F2E5C] mb-0.5">{stat.value}</div>
                              <div className="text-xs text-gray-600">{stat.label}</div>
                            </div>
                          );
                        })}
                    </div>

                    <div className="relative bg-white/60 backdrop-blur-sm rounded-2xl p-6 border border-white/60 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        {(() => {
                          const VisionIcon = iconMap[storyMissionVisionData.vision.icon] || Target;
                          return <VisionIcon className="w-5 h-5 text-[#E87D3E]" />;
                        })()}
                        <span className="text-sm font-bold text-[#2F2E5C]">{storyMissionVisionData.vision.title}</span>
                      </div>
                      <div
                        className="text-sm leading-relaxed text-gray-600 [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold"
                        dangerouslySetInnerHTML={{ __html: storyMissionVisionData.vision.text }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {storyCoreValuesData && storyCoreValuesData.visible !== false && (
              <>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                  {storyCoreValuesData.coreValues && storyCoreValuesData.coreValues
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((value: any) => {
                      const ValueIcon = iconMap[value.icon] || Shield;
                      return (
                        <div key={value.id} className="group relative bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/60 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all overflow-hidden">
                          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-gray-100/50 to-transparent rounded-full -mr-10 -mt-10" />
                          <div className="relative z-10">
                            <div className={`w-12 h-12 bg-gradient-to-br ${value.gradient} rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform`}>
                              <ValueIcon className="w-5 h-5" />
                            </div>
                            <h3 className="text-base font-bold text-[#2F2E5C] mb-2">{value.title}</h3>
                            <div
                              className="text-sm text-gray-600 leading-relaxed [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold"
                              dangerouslySetInnerHTML={{ __html: value.description }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>

                {storyCoreValuesData.differentiators && (
                  <div className="mt-8 relative bg-gradient-to-br from-[#E87D3E]/10 via-white/80 to-[#E8B84D]/10 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/60 shadow-xl overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#E8B84D]/10 to-transparent rounded-full blur-3xl" />
                    <div className="relative z-10">
                      <div className="text-center mb-6">
                        <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-xl px-4 py-2 rounded-full mb-3 border border-white/60 shadow-md">
                          {(() => {
                            const BadgeIcon = iconMap[storyCoreValuesData.differentiators.badgeIcon] || Zap;
                            return <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />;
                          })()}
                          <span className="text-sm font-bold text-[#2F2E5C]">{storyCoreValuesData.differentiators.badge}</span>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-3 gap-6">
                        {storyCoreValuesData.differentiators.items && storyCoreValuesData.differentiators.items
                          .sort((a: any, b: any) => a.order - b.order)
                          .map((item: any) => {
                            const DiffIcon = iconMap[item.icon] || Target;
                            return (
                              <div key={item.id} className="text-center">
                                <div className="inline-flex items-center justify-center w-14 h-14 bg-white/90 backdrop-blur-sm rounded-2xl border border-white/60 shadow-lg mb-4">
                                  <div className="text-[#E87D3E]"><DiffIcon className="w-5 h-5" /></div>
                                </div>
                                <div className="text-lg font-bold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent mb-2">{item.stat}</div>
                                <h4 className="font-bold text-[#2F2E5C] mb-2">{item.title}</h4>
                                <div
                                  className="text-sm text-gray-600 leading-relaxed [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold"
                                  dangerouslySetInnerHTML={{ __html: item.description }}
                                />
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      )}

      {/* Our People */}
      {sectionsLoading ? (
        <PeopleSkeleton />
      ) : peopleOverviewData && peopleOverviewData.visible !== false ? (
        <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]">
            <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
          </div>
          <div className="absolute inset-0 opacity-[0.012]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
                {(() => { const BadgeIcon = iconMap[peopleOverviewData.badgeIcon] || Users; return <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />; })()}
                <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{peopleOverviewData.badge}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
                {peopleOverviewData.title} <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{peopleOverviewData.highlightedTitle}</span>
              </h2>
              <div
                className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold"
                dangerouslySetInnerHTML={{ __html: peopleOverviewData.subtitle }}
              />
            </div>

            <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-10 border border-white/60 shadow-2xl mb-10 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#E8B84D]/10 to-transparent rounded-full blur-2xl" />
              <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
                <div className="relative rounded-2xl overflow-hidden shadow-xl">
                  <img src={peopleOverviewData.teamImage} alt="Team" className="w-full h-[400px] object-cover" loading="lazy" decoding="async" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2F2E5C]/40 to-transparent" />
                </div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl sm:text-2xl font-bold text-[#2F2E5C] mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-xl flex items-center justify-center shadow-lg">
                        {(() => { const TeamTitleIcon = iconMap[peopleOverviewData.teamTitleIcon] || Users; return <TeamTitleIcon className="w-5 h-5 text-white" />; })()}
                      </div>
                      {peopleOverviewData.teamTitle}
                    </h3>
                    <div
                      className="text-gray-600 leading-relaxed [&_strong]:font-bold [&_.gradient-text]:bg-gradient-to-r [&_.gradient-text]:from-[#E87D3E] [&_.gradient-text]:to-[#E8B84D] [&_.gradient-text]:bg-clip-text [&_.gradient-text]:text-transparent [&_.gradient-text]:font-bold [&_p]:mb-4 [&_p:last-child]:mb-0"
                      dangerouslySetInnerHTML={{ __html: peopleOverviewData.teamDescription }}
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {peopleOverviewData.teamStats && peopleOverviewData.teamStats
                      .sort((a: any, b: any) => a.order - b.order)
                      .map((stat: any) => {
                        const StatIcon = iconMap[stat.icon] || Users;
                        return (
                          <div key={stat.id} className="bg-white/70 backdrop-blur-sm rounded-xl p-3 border border-white/60 shadow-md text-center">
                            <div className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-lg mb-2 text-white">
                              <StatIcon className="w-4 h-4" />
                            </div>
                            <div className="text-lg font-bold text-[#2F2E5C]">{stat.value}</div>
                            <div className="text-xs text-gray-600">{stat.label}</div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>

            {peopleExpertiseData && peopleExpertiseData.visible !== false && (
              <div className="mb-10">
                <div className="text-center mb-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-[#2F2E5C] mb-3">{peopleExpertiseData.title}</h3>
                  <p className="text-sm text-gray-600 max-w-2xl mx-auto">{peopleExpertiseData.subtitle}</p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {peopleExpertiseData.expertiseAreas && peopleExpertiseData.expertiseAreas
                    .sort((a: any, b: any) => a.order - b.order)
                    .map((area: any) => {
                      const AreaIcon = iconMap[area.icon] || Cog;
                      return (
                        <div key={area.id} className="group relative bg-white/80 backdrop-blur-xl rounded-2xl p-6 border border-white/60 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all overflow-hidden">
                          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-gray-100/50 to-transparent rounded-full -mr-12 -mt-12" />
                          <div className="relative z-10">
                            <div className={`w-12 h-12 bg-gradient-to-br ${area.gradient} rounded-xl flex items-center justify-center mb-4 text-white shadow-lg group-hover:scale-110 transition-transform`}>
                              <AreaIcon className="w-5 h-5" />
                            </div>
                            <h4 className="font-bold text-[#2F2E5C] mb-3">{area.title}</h4>
                            <div className="space-y-1.5">
                              {area.skills.map((skill: string, j: number) => (
                                <div key={j} className="flex items-center gap-2 text-sm text-gray-600">
                                  <div className="w-1 h-1 bg-[#E87D3E] rounded-full" />
                                  <span>{skill}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {peopleCultureData && peopleCultureData.visible !== false && (
              <>
                <div className="relative bg-gradient-to-br from-[#2F2E5C] to-[#1a1935] rounded-3xl p-6 sm:p-8 lg:p-10 overflow-hidden shadow-2xl">
                  <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#E87D3E]/20 via-[#E8B84D]/10 to-transparent rounded-full blur-3xl" />
                  <div className="absolute bottom-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-2xl" />
                  <div className="relative z-10 grid lg:grid-cols-2 gap-8 items-center">
                    <div className="relative rounded-2xl overflow-hidden shadow-2xl order-2 lg:order-1">
                      <img src={peopleCultureData.cultureImage} alt="Team Culture" className="w-full h-[350px] object-cover" loading="lazy" decoding="async" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#2F2E5C]/60 to-transparent" />
                    </div>
                    <div className="space-y-6 order-1 lg:order-2">
                      <div>
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-full mb-4 border border-white/30">
                          {(() => { const BadgeIcon = iconMap[peopleCultureData.cultureBadgeIcon] || Shield; return <BadgeIcon className="w-4 h-4 text-[#E8B84D]" />; })()}
                          <span className="text-xs font-bold text-white/90">{peopleCultureData.cultureBadge}</span>
                        </div>
                        <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">{peopleCultureData.cultureTitle}</h3>
                        <p className="text-white/80 leading-relaxed mb-6">{peopleCultureData.cultureDescription}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        {peopleCultureData.culturePillars && peopleCultureData.culturePillars
                          .sort((a: any, b: any) => a.order - b.order)
                          .map((pillar: any) => {
                            const PillarIcon = iconMap[pillar.icon] || Sparkles;
                            return (
                              <div key={pillar.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                                <div className="w-8 h-8 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-lg flex items-center justify-center mb-2 text-white">
                                  <PillarIcon className="w-4 h-4" />
                                </div>
                                <h4 className="text-sm font-bold text-white mb-1">{pillar.title}</h4>
                                <p className="text-xs text-white/70">{pillar.description}</p>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </div>

                {peopleCultureData.recruitment && peopleCultureData.recruitment.enabled && (
                  <div className="mt-10 text-center">
                    <div className="inline-flex flex-col items-center gap-3 bg-white/70 backdrop-blur-xl rounded-2xl p-6 border border-white/60 shadow-lg">
                      <div className="flex items-center gap-2">
                        {(() => { const RecruitIcon = iconMap[peopleCultureData.recruitment.icon] || Sparkles; return <RecruitIcon className="w-5 h-5 text-[#E87D3E]" />; })()}
                        <span className="font-bold text-[#2F2E5C]">{peopleCultureData.recruitment.title}</span>
                      </div>
                      <p className="text-sm text-gray-600 max-w-md">{peopleCultureData.recruitment.description}</p>
                      <Link to={peopleCultureData.recruitment.buttonLink}>
                        <Button className="relative overflow-hidden bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] hover:from-[#E8B84D] hover:to-[#E87D3E] text-white rounded-full px-6 h-10 gap-2 shadow-lg shadow-[#E87D3E]/20">
                          <span className="relative z-10 flex items-center gap-2">
                            {peopleCultureData.recruitment.buttonText}
                            <ArrowRight className="w-4 h-4" />
                          </span>
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      ) : null}

      {/* Testimonials */}
      {(!orderLoading && sectionOrder.find(s => s.id === 'testimonials')?.visible !== false) && (
        <TestimonialsSection />
      )}

      {/* How We Work */}
      {sectionsLoading ? (
        <WorkSkeleton />
      ) : workProcessData && workProcessData.visible ? (
        <section className="relative py-12 sm:py-16 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-white via-gray-50 to-white overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-l from-[#E87D3E] to-[#E8B84D] rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] rounded-full blur-3xl" />
          </div>
          <div className="absolute inset-0 opacity-[0.012]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
                {(() => { const BadgeIcon = iconMap[workProcessData.badgeIcon] || Workflow; return <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />; })()}
                <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{workProcessData.badge}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 text-[#2F2E5C]">
                {workProcessData.title} <span className="bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">{workProcessData.highlightedTitle}</span>
              </h2>
              <p className="text-base text-gray-600 max-w-3xl mx-auto leading-relaxed">{workProcessData.subtitle}</p>
            </div>

            <div className="relative mb-10">
              <div className="hidden lg:block absolute top-20 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-[#E87D3E]/20 via-[#E8B84D]/40 to-[#E87D3E]/20" />
              <div className={`grid gap-6 lg:gap-4 ${
                workProcessData.processSteps.length === 5 ? 'sm:grid-cols-2 lg:grid-cols-5' :
                workProcessData.processSteps.length === 4 ? 'sm:grid-cols-2 lg:grid-cols-4' :
                workProcessData.processSteps.length === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' :
                workProcessData.processSteps.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
              }`}>
                {workProcessData.processSteps.map((item: any) => {
                  const StepIcon = iconMap[item.icon] || Sparkles;
                  return (
                    <div key={item.id} className="relative group">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                        <div className={`w-10 h-10 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center text-white font-bold text-sm shadow-lg group-hover:scale-110 transition-transform border-2 border-white`}>
                          {item.step}
                        </div>
                      </div>
                      <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl p-6 pt-10 border border-white/60 shadow-md hover:shadow-2xl hover:-translate-y-2 transition-all overflow-hidden h-full">
                        <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-gray-100/50 to-transparent rounded-full -mr-10 -mt-10" />
                        <div className="relative z-10 text-center">
                          <div className={`w-12 h-12 bg-gradient-to-br ${item.gradient} rounded-xl flex items-center justify-center mx-auto mb-4 text-white shadow-md`}>
                            <StepIcon className="w-5 h-5" />
                          </div>
                          <h3 className="font-bold text-[#2F2E5C] mb-3">{item.title}</h3>
                          <p className="text-sm text-gray-600 leading-relaxed mb-4">{item.description}</p>
                          <div className="space-y-2">
                            {item.details.map((detail: string, j: number) => (
                              <div key={j} className="flex items-center gap-2 text-xs text-gray-500">
                                <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${item.gradient}`} />
                                <span>{detail}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {workBenefitsData && workBenefitsData.visible && (
              <>
                <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl p-6 sm:p-8 border border-white/60 shadow-xl mb-10 overflow-hidden">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-gradient-to-bl from-[#E8B84D]/10 to-transparent rounded-full blur-2xl" />
                  <div className="relative z-10">
                    <div className="text-center mb-6">
                      <h3 className="text-lg sm:text-xl font-bold text-[#2F2E5C] mb-2">{workBenefitsData.highlightsTitle}</h3>
                      <p className="text-sm text-gray-600">{workBenefitsData.highlightsSubtitle}</p>
                    </div>
                    <div className={`grid gap-6 ${
                      workBenefitsData.highlights.length === 4 ? 'sm:grid-cols-4' :
                      workBenefitsData.highlights.length === 3 ? 'sm:grid-cols-3' :
                      workBenefitsData.highlights.length === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
                    }`}>
                      {workBenefitsData.highlights.map((stat: any) => {
                        const StatIcon = iconMap[stat.icon] || Sparkles;
                        return (
                          <div key={stat.id} className="text-center group">
                            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-2xl mb-3 text-white shadow-lg group-hover:scale-110 transition-transform">
                              <StatIcon className="w-5 h-5" />
                            </div>
                            <div className="text-xl sm:text-2xl font-bold text-[#2F2E5C] mb-1">{stat.value}</div>
                            <div className="text-xs font-bold text-[#E87D3E] mb-1">{stat.label}</div>
                            <p className="text-xs text-gray-600">{stat.description}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="relative bg-gradient-to-br from-[#2F2E5C] to-[#1a1935] rounded-3xl p-6 sm:p-8 overflow-hidden shadow-2xl mb-10">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-[#E87D3E]/20 via-[#E8B84D]/10 to-transparent rounded-full blur-3xl" />
                  <div className="relative z-10">
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-3 border border-white/30">
                        {(() => { const BadgeIcon = iconMap[workBenefitsData.expectationsBadgeIcon] || Sparkles; return <BadgeIcon className="w-4 h-4 text-[#E8B84D]" />; })()}
                        <span className="text-xs font-bold text-white/90">{workBenefitsData.expectationsBadge}</span>
                      </div>
                      <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{workBenefitsData.expectationsTitle}</h3>
                      <p className="text-white/80 text-sm max-w-2xl mx-auto">{workBenefitsData.expectationsSubtitle}</p>
                    </div>
                    <div className={`grid gap-4 ${
                      workBenefitsData.expectations.length >= 6 ? 'sm:grid-cols-2 lg:grid-cols-3' :
                      workBenefitsData.expectations.length >= 4 ? 'sm:grid-cols-2' : 'sm:grid-cols-1'
                    }`}>
                      {workBenefitsData.expectations.map((item: any) => {
                        const ExpectIcon = iconMap[item.icon] || Sparkles;
                        return (
                          <div key={item.id} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:bg-white/15 transition-all">
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] rounded-lg flex items-center justify-center flex-shrink-0 text-white shadow-md">
                                <ExpectIcon className="w-5 h-5" />
                              </div>
                              <div>
                                <h4 className="text-sm font-bold text-white mb-1">{item.title}</h4>
                                <p className="text-xs text-white/70">{item.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="text-center">
                  <div className="inline-flex flex-col items-center gap-4">
                    <p className="text-sm text-gray-600 max-w-md">{workBenefitsData.cta.text}</p>
                    <Link to={workBenefitsData.cta.buttonLink}>
                      <Button className="relative overflow-hidden bg-gradient-to-r from-[#D74444] via-[#e85e5e] to-[#c13939] hover:from-[#c13939] hover:via-[#D74444] hover:to-[#e85e5e] text-white rounded-full px-8 h-12 gap-2 shadow-lg shadow-[#D74444]/20 before:absolute before:inset-0 before:bg-white/10 before:opacity-0 hover:before:opacity-100 before:transition-opacity">
                        <span className="relative z-10 flex items-center gap-2">
                          {workBenefitsData.cta.buttonText}
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </Button>
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      ) : null}

      {/* Dynamic footer sections */}
      {renderDynamicSections()}
    </>
  );
}